import {_} from 'lodash';
import * as ko from "knockout";
import {IMessageData, ISystem, ISystemRole} from "../../../index";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {IDialogOptions} from "../../dialog/index";
import {nsSystemAccessSearch} from "../roles/search/nsSystemAccessSearch";
import {MOVING_MESSAGE, PLEASE_WAIT_MESSAGE} from "../../../constants/Language";
import PromiseRequest from "../../../req/PromiseRequest";
import {global} from "../../../appContext";
import {MAX_RESULTS_PER_PAGE, SPINNER} from "../../../constants";
import {ButtonManager} from "../../../util/ButtonManager";
import {getCancelButton, getContinueButton, getExitButton} from "../../../constants/RouteDefinitions";
import {createErrorString, isPartOf} from "../../../util/misc";
import pubsub from "../../../pubsub";
import {getSearchSystemParams} from "../../../util/request";
import move_rolesHtml from './move-roles.html?raw';

export const template = move_rolesHtml;

interface IParams {
    dialog: IDialogOptions
    roles: ISystemRole[]
    system: ISystem
}

type TStep = 'init' | 'introduction' | 'process' | 'finish'

export class viewModel {
    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly roles: ISystemRole[]
    private readonly step: KnockoutObservable<TStep | null> = ko.observable(null);
    private readonly req_searchsystem: PromiseRequest = global.getPromiseRequest('searchsystem');
    private systems!: ISystem[];
    private system: ISystem;
    private availableSystems!: ISystem[];
    private selectedSystem: KnockoutObservable<ISystem | null> = ko.observable(null)
    private readonly pageButtons: ButtonManager = new ButtonManager()
    private requests!: PromiseRequest[]
    private submitError!: {reason: string, role: ISystemRole}[]
    private submitSuccess!: ISystemRole[]

    constructor(private params: IParams) {
        this.roles = _.sortBy(params.roles, (o: ISystemRole) => o.name.toLowerCase())
        this.system = params.system
        this.kosub.subscribe(this.step, this.onStep.bind(this))
        this.step('init')

        this.pageButtons.add(getCancelButton(() => params.dialog.doClose?.(), {validator: () => this.isStep('init', 'introduction')}))

        this.pageButtons.add(getContinueButton({
            onClick: () => {
                if (this.isStep('introduction')) {
                    this.step('process')
                }
            },
            enabled: () => !!this.selectedSystem(),
            validator: () => !!(this.isStep('introduction') && this.isReady() && this.roles.length),
        }))

        this.pageButtons.add(getExitButton(() => params.dialog.doClose?.(), {
            validator: () => this.isStep('finish'),
        }))
    }

    private onStep() {
        switch (this.step()) {
            case "init": {
                this.isReady(false)
                this.messageData({message: PLEASE_WAIT_MESSAGE})
                this.req_searchsystem
                    .request(getSearchSystemParams({
                        resultsprpage: MAX_RESULTS_PER_PAGE,
                    }))
                    .then((data: ISystem[]) => {
                        this.systems = data
                        this.availableSystems = _.sortBy(this.systems.filter((o: ISystem) => {
                            const a = o.multiplerolesamecompany == this.system.multiplerolesamecompany
                            const b = o.multiplerolesamedepartment == this.system.multiplerolesamedepartment
                            const c = o.prioritybasedroles == this.system.prioritybasedroles
                            const d = o.purgemanualdepartmentpermissions == this.system.purgemanualdepartmentpermissions
                            const e = o.id != this.system.id
                            const f = !o.definitionreadonly
                            return a && b && c && d && e && f
                        }), (o: ISystem) => o.name.toLowerCase())
                        this.step('introduction')
                    })
                break
            }
            case 'introduction': {
                this.messageData(null)
                this.isReady(true)
                break
            }
            case 'process': {
                this.params.dialog.isClosable?.(false)
                this.isReady(true)
                this.messageData({message: MOVING_MESSAGE})

                this.requests?.forEach(o => o.abort())
                this.requests = []
                this.submitError = []
                this.submitSuccess = []

                const fnArr: (() => Promise<any>)[] = this.roles.map((role: ISystemRole) => {
                    return () => {
                        const req_updaterole = new PromiseRequest('updaterole')
                        const roleParam = _.clone(role)
                        roleParam.systemid = this.selectedSystem()!.id
                        roleParam.memberofgroups = roleParam.memberofgroups.split(',').filter(Boolean).join(',') // fix BE-bug which leaves a trailing ','
                        roleParam.rolememberrulesets = roleParam.rolememberrulesets.split(',').filter(Boolean).join(',') // fix BE-bug which leaves a trailing ','
                        this.requests.push(req_updaterole)
                        return Promise.resolve()
                            .then(() => this.messageData({message: `((((moving)) ((permission|ac)) [${role.name}]|td)) ${SPINNER}`}))
                            .then(() => req_updaterole.request({
                                jrole: JSON.stringify(roleParam),
                            }))
                            .then(() => {
                                this.submitSuccess.push(role)
                            })
                            .catch((reason: string) => {
                                this.submitError.push({
                                    reason: createErrorString(reason, '_blank'),
                                    role: role,
                                })
                            })
                    }
                })

                const doNext = (): Promise<any> => {
                    if (!fnArr.length) {
                        return Promise.resolve()
                    }
                    const fn = fnArr.shift()
                    return fn!()
                        .then(() => {
                            if (fnArr.length) {
                                return doNext()
                            }
                        })
                }
                doNext()
                    .then(() => {
                        this.step("finish")
                    })

                break
            }
            case 'finish': {
                global.cacheManager.removeCache('searchsystem')
                global.cacheManager.removeCache('searchobject')
                global.cacheManager.removeCache('getobject')
                this.messageData(null)
                this.isReady(true)
                this.params.dialog.isClosable?.(true)
                this.params.dialog.onSuccess?.()
                pubsub.trigger('collection.needs.refresh')
                break
            }
        }
    }

    private isStep(...args: TStep[]) {
        const step = this.step();
        return step != null && isPartOf<TStep>(step, ...args)
    }

    private numAffectedObjectsText(num: number = this.roles.length): string {
        return nsSystemAccessSearch.numAffectedObjectsText(num)
    }

    dispose() {
        this.kosub.unsuscribeAll()
        this.requests?.forEach(o => o.abort())
    }

}
